import {
  AfterViewInit,
  Directive,
  ElementRef,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Renderer2,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
import { ControlsConfigService } from '../../injection/controls-config.service';
import { Color, COLOR, NormalCircle } from '@seech/shared-ng';
import { ThemeColorDirective } from '../theme/theme.directive';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
@Directive({
  selector: '[sch-checkbox]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxDirective),
      multi: true,
    },
  ],
  hostDirectives: [
    {
      directive: ThemeColorDirective,
      inputs: ['color'],
    },
  ],
})
export class CheckboxDirective
  implements AfterViewInit, OnChanges, OnInit, OnDestroy, ControlValueAccessor
{
  @Input({ required: true }) id = '';
  @Input() color: Color = COLOR.PRIMARY;
  @Input() label = '';
  @Input() labelColor = '#97A2B0';
  @Input() labelFontSize = '14px';
  @Input() labelFontWeight = '400';
  @Input() shape!: NormalCircle;
  @Input() highlightSelectedLabel = true;
  private labelElement!: HTMLElement;

  checkboxId!: string;

  private onChange = (value: boolean) => {};

  private onTouched = () => {};

  private removeChangeListener?: () => void;
  private removeBlurListener?: () => void;

  constructor(
    private configService: ControlsConfigService,
    private elRef: ElementRef,
    private renderer: Renderer2
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['id']) {
      this.checkboxId = `checkbox-${this.id}`;
    }
    if (
      changes['label'] &&
      !changes['label'].firstChange &&
      this.labelElement
    ) {
      this.labelElement.textContent = this.label;
    }
  }

  ngOnInit(): void {
    if (!this.shape) {
      const config = this.configService.getConfig();
      this.shape = config?.radioButtonShape
        ? config.radioButtonShape
        : 'normal';
    }
  }

  ngAfterViewInit(): void {
    this.renderer.addClass(this.elRef.nativeElement, 'form-check');
    this.renderer.addClass(this.elRef.nativeElement, 'form-checkbox');
    this.renderer.addClass(this.elRef.nativeElement, 'seech-checkbox');

    const inputEl = this.elRef.nativeElement.querySelector('input');
    this.renderer.addClass(inputEl, 'form-check-input');
    this.renderer.addClass(inputEl, this.shape);
    this.renderer.setAttribute(inputEl, 'id', this.checkboxId);

    // Listen for changes to propagate to Angular forms and update label styling
    this.removeChangeListener = this.renderer.listen(
      inputEl,
      'change',
      (event: Event) => {
        const target = event.target as HTMLInputElement;
        const isChecked = !!target?.checked;
        // propagate to form
        this.onChange(isChecked);
        // update label highlight if enabled
        if (this.highlightSelectedLabel) {
          const labelEl = this.elRef.nativeElement.querySelector('label');
          if (labelEl) {
            isChecked
              ? this.renderer.addClass(labelEl, `text-${this.color}`)
              : this.renderer.removeClass(labelEl, `text-${this.color}`);
          }
        }
      }
    );

    // Mark as touched on blur
    this.removeBlurListener = this.renderer.listen(
      inputEl,
      'blur',
      () => this.onTouched()
    );

    if (this.label) {
      this.labelElement = this.createLabelElement();
      this.renderer.appendChild(this.elRef.nativeElement, this.labelElement);
    }
  }

  private createLabelElement(): HTMLElement {
    const label = this.renderer.createElement('label') as HTMLElement;
    label.style.color = this.labelColor;
    label.style.fontSize = this.labelFontSize;
    label.style.fontWeight = this.labelFontWeight;
    label.style.paddingLeft = '5px';
    label.textContent = this.label;
    label.setAttribute('for', this.checkboxId);
    return label;
  }

  writeValue(value: boolean): void {
    const inputEl = this.elRef.nativeElement.querySelector('input');
    const isChecked = !!value;
    this.renderer.setProperty(inputEl, 'checked', isChecked);
    if (this.highlightSelectedLabel) {
      const labelEl = this.elRef.nativeElement.querySelector('label');
      if (labelEl) {
        isChecked
          ? this.renderer.addClass(labelEl, `text-${this.color}`)
          : this.renderer.removeClass(labelEl, `text-${this.color}`);
      }
    }
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    const inputEl = this.elRef.nativeElement.querySelector('input');
    if (inputEl) {
      this.renderer.setProperty(inputEl, 'disabled', !!isDisabled);
    }
  }

  ngOnDestroy(): void {
    if (this.removeChangeListener) {
      this.removeChangeListener();
    }
    if (this.removeBlurListener) {
      this.removeBlurListener();
    }
  }
}
